import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Trophy, Plus, ArrowLeft, Calendar, Medal, AlertTriangle, Target, CreditCard, Gavel, Eye, Trash, Edit, ChevronRight, ExternalLink, Sparkles, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Textarea } from '@/components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const APP_URL = process.env.REACT_APP_BACKEND_URL;

export default function TorneosSection({ ligaId }) {
  const [categorias, setCategorias] = useState([]);
  const [torneos, setTorneos] = useState([]);
  const [selectedTorneo, setSelectedTorneo] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (ligaId) {
      loadData();
    }
  }, [ligaId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [catsRes, torneosRes] = await Promise.all([
        axios.get(`${API}/categorias?liga_id=${ligaId}`, { withCredentials: true }),
        axios.get(`${API}/torneos?liga_id=${ligaId}`, { withCredentials: true })
      ]);
      setCategorias(catsRes.data);
      setTorneos(torneosRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  if (selectedTorneo) {
    return <TorneoDetail torneo={selectedTorneo} categorias={categorias} onBack={() => { setSelectedTorneo(null); loadData(); }} />;
  }

  return (
    <div data-testid="torneos-section">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-white">Torneos</h2>
        <Button onClick={() => setShowCreateDialog(true)} className="gap-2 bg-white text-slate-900 hover:bg-slate-200" data-testid="create-torneo-btn">
          <Plus className="w-4 h-4" /> Nuevo Torneo
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin" /></div>
      ) : torneos.length === 0 ? (
        <Card className="p-8 text-center bg-slate-800 border-slate-700">
          <Trophy className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <p className="text-white font-medium">Sin torneos</p>
          <p className="text-sm text-slate-400 mt-1">Crea un torneo para una categoria</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {torneos.map(torneo => {
            const cat = categorias.find(c => c.id === torneo.categoria_id);
            return (
              <Card
                key={torneo.id}
                className="p-4 bg-slate-800 border-slate-700 hover:bg-slate-750 cursor-pointer transition-colors"
                onClick={() => setSelectedTorneo(torneo)}
                data-testid={`torneo-card-${torneo.id}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Trophy className="w-5 h-5 text-white" />
                      <h3 className="font-bold text-white">{torneo.nombre}</h3>
                      <span className={`px-2 py-0.5 text-xs rounded ${torneo.estado === 'activo' ? 'bg-emerald-900/50 text-emerald-400' : torneo.estado === 'borrador' ? 'bg-amber-900/50 text-amber-400' : 'bg-slate-700 text-slate-400'}`}>
                        {torneo.estado === 'activo' ? 'Activo' : torneo.estado === 'borrador' ? 'Borrador' : 'Finalizado'}
                      </span>
                    </div>
                    <p className="text-sm text-slate-400">
                      Categoria: {cat?.name || 'N/A'}
                      {torneo.campeon && <span className="ml-3 text-amber-400">Campeon: {torneo.campeon}</span>}
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-500" />
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <CreateTorneoDialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        categorias={categorias}
        torneos={torneos}
        ligaId={ligaId}
        onCreated={() => { setShowCreateDialog(false); loadData(); }}
      />
    </div>
  );
}


function CreateTorneoDialog({ open, onClose, categorias, torneos, ligaId, onCreated }) {
  const [step, setStep] = useState(1); // 1: config, 2: fixture IA, 3: confirmar
  const [nombre, setNombre] = useState('');
  const [categoriaId, setCategoriaId] = useState('');
  const [amarillas, setAmarillas] = useState(5);
  const [expulsionGlobal, setExpulsionGlobal] = useState(false);
  const [loading, setLoading] = useState(false);

  // AI Fixture states
  const [prompt, setPrompt] = useState('');
  const [generando, setGenerando] = useState(false);
  const [fixturePreview, setFixturePreview] = useState(null);
  const [equiposDisponibles, setEquiposDisponibles] = useState([]);
  const [tempTorneoId, setTempTorneoId] = useState(null);

  const SUGGESTIONS = [
    'Todos contra todos, solo ida',
    'Todos contra todos, ida y vuelta',
    '2 grupos, clasifican 2 por grupo, luego semifinal y final',
    'Eliminacion directa (llaves)'
  ];

  const categoriasDisponibles = categorias.filter(cat =>
    !torneos.some(t => t.categoria_id === cat.id && t.estado === 'activo')
  );

  const handleClose = () => {
    // If we created a temp torneo but user cancelled, delete it
    if (tempTorneoId) {
      axios.delete(`${API}/torneos/${tempTorneoId}`, { withCredentials: true }).catch(() => {});
    }
    setStep(1); setNombre(''); setCategoriaId(''); setAmarillas(5);
    setExpulsionGlobal(false); setPrompt(''); setFixturePreview(null);
    setTempTorneoId(null); setEquiposDisponibles([]);
    onClose();
  };

  // Step 1 -> Step 2: Create torneo in "borrador" state, then go to fixture gen
  const handleNextToFixture = async () => {
    if (!nombre.trim() || !categoriaId) { toast.error('Completa nombre y categoria'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/torneos`, {
        liga_id: ligaId, categoria_id: categoriaId, nombre,
        amarillas_para_castigo: amarillas, expulsion_afecta_otros_torneos: expulsionGlobal,
        estado: 'borrador'
      }, { withCredentials: true });
      setTempTorneoId(res.data.id);
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerarFixture = async () => {
    if (!prompt.trim()) { toast.error('Describe el formato del torneo'); return; }
    setGenerando(true);
    setFixturePreview(null);
    try {
      const res = await axios.post(`${API}/torneos/${tempTorneoId}/generar-fixture-ia`, { prompt }, { withCredentials: true });
      if (res.data.success) {
        setFixturePreview(res.data);
        setEquiposDisponibles(res.data.equipos || []);
      } else {
        toast.error(res.data.error || 'Error al generar fixture');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al generar fixture');
    } finally {
      setGenerando(false);
    }
  };

  // Move a match from one fecha to another
  const moverPartido = (fromFechaIdx, partidoIdx, toFechaIdx) => {
    if (!fixturePreview) return;
    const newFixture = JSON.parse(JSON.stringify(fixturePreview.fixture));
    const partido = newFixture.fechas[fromFechaIdx].partidos.splice(partidoIdx, 1)[0];
    newFixture.fechas[toFechaIdx].partidos.push(partido);
    // Remove empty fechas
    newFixture.fechas = newFixture.fechas.filter(f => f.partidos.length > 0);
    setFixturePreview({
      ...fixturePreview,
      fixture: newFixture,
      total_fechas: newFixture.fechas.length,
      total_partidos: newFixture.fechas.reduce((sum, f) => sum + f.partidos.length, 0)
    });
  };

  // Confirm: apply fixture and activate tournament
  const handleConfirmar = async () => {
    if (!fixturePreview?.fixture) { toast.error('Genera un fixture primero'); return; }
    setLoading(true);
    try {
      // Apply fixture
      await axios.post(`${API}/torneos/${tempTorneoId}/aplicar-fixture-ia`, {
        fixture: fixturePreview.fixture
      }, { withCredentials: true });
      // Activate tournament
      await axios.put(`${API}/torneos/${tempTorneoId}`, {
        estado: 'activo'
      }, { withCredentials: true });
      toast.success('Torneo creado y activado con fixture');
      handleClose();
      onCreated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear torneo');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-slate-800 border-slate-700 max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="create-torneo-dialog">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            {step === 1 && <><Trophy className="w-5 h-5" /> Nuevo Torneo - Configuracion</>}
            {step === 2 && <><Sparkles className="w-5 h-5 text-purple-400" /> Generar Fixture con IA</>}
          </DialogTitle>
        </DialogHeader>

        {/* Step indicator */}
        <div className="flex items-center gap-2 mb-2">
          {[1, 2].map(s => (
            <div key={s} className={`flex items-center gap-1.5 ${s <= step ? 'text-white' : 'text-slate-600'}`}>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${s < step ? 'bg-emerald-600' : s === step ? 'bg-purple-600' : 'bg-slate-700'}`}>{s}</div>
              <span className="text-xs">{s === 1 ? 'Config' : 'Fixture'}</span>
              {s < 2 && <div className={`w-8 h-0.5 ${s < step ? 'bg-emerald-600' : 'bg-slate-700'}`} />}
            </div>
          ))}
        </div>

        {/* STEP 1: Configuration */}
        {step === 1 && (
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300">Nombre del Torneo</Label>
              <Input value={nombre} onChange={(e) => setNombre(e.target.value)}
                className="bg-slate-700 border-slate-600 text-white" placeholder="Ej: Campeonato Apertura 2026"
                data-testid="torneo-nombre-input" />
            </div>
            <div>
              <Label className="text-slate-300">Categoria</Label>
              <Select value={categoriaId} onValueChange={setCategoriaId}>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white" data-testid="torneo-categoria-select">
                  <SelectValue placeholder="Selecciona categoria" />
                </SelectTrigger>
                <SelectContent position="popper" sideOffset={5}>
                  {categoriasDisponibles.length === 0 ? (
                    <SelectItem value="none" disabled>Todas las categorias tienen torneo activo</SelectItem>
                  ) : (
                    categoriasDisponibles.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300">Amarillas para castigo (1 fecha)</Label>
              <Input type="number" min={1} max={20} value={amarillas} onChange={(e) => setAmarillas(parseInt(e.target.value))}
                className="bg-slate-700 border-slate-600 text-white" data-testid="torneo-amarillas-input" />
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-700 rounded-lg">
              <div>
                <p className="text-sm font-medium text-white">Expulsion afecta otros torneos</p>
                <p className="text-xs text-slate-400">El castigo aplica en todos los torneos de la categoria</p>
              </div>
              <Switch checked={expulsionGlobal} onCheckedChange={setExpulsionGlobal} data-testid="torneo-expulsion-switch" />
            </div>
            <Button onClick={handleNextToFixture} className="w-full bg-purple-600 hover:bg-purple-700 text-white gap-2"
              disabled={loading || !categoriaId || !nombre.trim()} data-testid="torneo-next-fixture-btn">
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Creando...</> : <><Sparkles className="w-4 h-4" /> Siguiente: Generar Fixture</>}
            </Button>
          </div>
        )}

        {/* STEP 2: AI Fixture Generation */}
        {step === 2 && (
          <div className="space-y-4">
            {!fixturePreview ? (
              <>
                <div>
                  <Label className="text-slate-300 mb-2 block">Describe el formato del torneo</Label>
                  <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)}
                    className="bg-slate-700 border-slate-600 text-white min-h-[80px]"
                    placeholder="Ej: Todos contra todos ida y vuelta, 2 grupos de 4 equipos..."
                    data-testid="ia-prompt-input" />
                </div>
                <div className="space-y-2">
                  <p className="text-xs text-slate-500">Sugerencias:</p>
                  <div className="flex flex-wrap gap-2">
                    {SUGGESTIONS.map(s => (
                      <button key={s} onClick={() => setPrompt(s)}
                        className="text-xs px-3 py-1.5 rounded-full bg-slate-700 text-slate-300 hover:bg-purple-600/30 hover:text-purple-300 transition-colors"
                        data-testid="ia-suggestion">
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
                <Button onClick={handleGenerarFixture} disabled={generando || !prompt.trim()}
                  className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white gap-2"
                  data-testid="ia-generate-btn">
                  {generando ? <><Loader2 className="w-4 h-4 animate-spin" /> Generando fixture...</> : <><Sparkles className="w-4 h-4" /> Generar Fixture</>}
                </Button>
                <Button variant="outline" className="w-full border-slate-600 text-slate-400" onClick={() => setStep(1)}>
                  <ArrowLeft className="w-4 h-4 mr-2" /> Volver a configuracion
                </Button>
              </>
            ) : (
              <>
                <div className="p-3 bg-emerald-900/20 border border-emerald-800 rounded-lg">
                  <p className="text-sm text-emerald-400 font-medium">Fixture generado</p>
                  <p className="text-xs text-slate-400 mt-1">
                    {fixturePreview.fixture.formato} | {fixturePreview.total_fechas} fechas, {fixturePreview.total_partidos} partidos | {equiposDisponibles.length} equipos
                  </p>
                </div>

                <div className="space-y-2 max-h-[35vh] overflow-y-auto pr-1">
                  {fixturePreview.fixture.fechas.map((fecha, i) => (
                    <div key={i} className="p-3 bg-slate-700 rounded-lg" data-testid={`ia-preview-fecha-${i}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <Calendar className="w-4 h-4 text-slate-400" />
                        <h5 className="text-sm font-semibold text-white">{fecha.nombre}</h5>
                        <span className={`text-xs px-1.5 py-0.5 rounded ${fecha.tipo === 'eliminacion' ? 'bg-purple-900/50 text-purple-400' : 'bg-blue-900/50 text-blue-400'}`}>
                          {fecha.tipo === 'eliminacion' ? 'Eliminacion' : 'Grupo'}
                        </span>
                        {fecha.grupo && <span className="text-xs bg-slate-600 px-1.5 py-0.5 rounded text-slate-300">Grupo {fecha.grupo}</span>}
                      </div>
                      <div className="space-y-1">
                        {fecha.partidos.map((p, j) => (
                          <div key={j} className="flex items-center justify-between text-sm text-slate-300 pl-6 group">
                            <span><span className="font-medium">{p.local}</span> <span className="text-slate-500 mx-1">vs</span> <span className="font-medium">{p.visitante}</span></span>
                            {fixturePreview.fixture.fechas.length > 1 && (
                              <select
                                className="opacity-0 group-hover:opacity-100 text-xs bg-slate-600 text-slate-300 rounded px-1 py-0.5 cursor-pointer transition-opacity"
                                value=""
                                onChange={(e) => { if (e.target.value !== '') moverPartido(i, j, parseInt(e.target.value)); }}
                                data-testid={`mover-partido-${i}-${j}`}>
                                <option value="">Mover a...</option>
                                {fixturePreview.fixture.fechas.map((f2, idx) => idx !== i && (
                                  <option key={idx} value={idx}>{f2.nombre}</option>
                                ))}
                              </select>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex gap-3">
                  <Button variant="outline" className="flex-1 border-slate-600 text-slate-300"
                    onClick={() => setFixturePreview(null)} data-testid="ia-regenerate-btn">
                    <Sparkles className="w-3.5 h-3.5 mr-1" /> Regenerar
                  </Button>
                  <Button onClick={handleConfirmar} disabled={loading}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white gap-2" data-testid="ia-confirm-btn">
                    {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Creando...</> : <><Trophy className="w-4 h-4" /> Confirmar y Activar</>}
                  </Button>
                </div>

                <p className="text-xs text-slate-500 text-center">
                  Al confirmar se crea el torneo activo con este fixture. Luego solo podras registrar resultados.
                </p>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


function TorneoDetail({ torneo, categorias, onBack }) {
  const [activeTab, setActiveTab] = useState('fechas');
  const [fechas, setFechas] = useState([]);
  const [posiciones, setPosiciones] = useState([]);
  const [goleadores, setGoleadores] = useState([]);
  const [tarjetas, setTarjetas] = useState([]);
  const [sanciones, setSanciones] = useState([]);
  const [showFechaDialog, setShowFechaDialog] = useState(false);
  const [showPartidoDialog, setShowPartidoDialog] = useState(false);
  const [showEventoDialog, setShowEventoDialog] = useState(false);
  const [showResultadoDialog, setShowResultadoDialog] = useState(false);
  const [showSancionDialog, setShowSancionDialog] = useState(false);
  const [showFinalizarDialog, setShowFinalizarDialog] = useState(false);
  const [selectedFecha, setSelectedFecha] = useState(null);
  const [selectedPartido, setSelectedPartido] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clubes, setClubes] = useState([]);

  const catName = categorias.find(c => c.id === torneo.categoria_id)?.name || '';

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [fechasRes, posRes, golRes, tarRes, sanRes, clubesRes] = await Promise.all([
        axios.get(`${API}/torneos/${torneo.id}/fechas`, { withCredentials: true }),
        axios.get(`${API}/torneos/${torneo.id}/tabla-posiciones`, { withCredentials: true }),
        axios.get(`${API}/torneos/${torneo.id}/goleadores`, { withCredentials: true }),
        axios.get(`${API}/torneos/${torneo.id}/tarjetas`, { withCredentials: true }),
        axios.get(`${API}/torneos/${torneo.id}/sanciones`, { withCredentials: true }),
        axios.get(`${API}/clubes?liga_id=${torneo.liga_id || ''}`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      setFechas(fechasRes.data);
      setPosiciones(posRes.data.posiciones || []);
      setGoleadores(golRes.data.goleadores || []);
      setTarjetas(tarRes.data.tarjetas || []);
      setSanciones(sanRes.data);
      setClubes(clubesRes.data);
    } catch (error) {
      toast.error('Error al cargar datos del torneo');
    } finally {
      setLoading(false);
    }
  };

  const equipos = clubes.flatMap(club =>
    club.categorias?.filter(c => c.categoria_id === torneo.categoria_id).map(c => c.nombre_corto) || []
  );

  const handleDeleteFecha = async (fechaId) => {
    if (!window.confirm('Eliminar esta fecha y todos sus partidos?')) return;
    try {
      await axios.delete(`${API}/torneos/fechas/${fechaId}`, { withCredentials: true });
      toast.success('Fecha eliminada');
      loadAll();
    } catch (error) { toast.error('Error al eliminar'); }
  };

  const handleDeletePartido = async (partidoId) => {
    if (!window.confirm('Eliminar este partido?')) return;
    try {
      await axios.delete(`${API}/torneos/partidos/${partidoId}`, { withCredentials: true });
      toast.success('Partido eliminado');
      loadAll();
    } catch (error) { toast.error('Error al eliminar'); }
  };

  const publicUrl = `${APP_URL}/torneo/${torneo.id}`;

  const TABS = [
    { id: 'fechas', label: 'Fixture', icon: Calendar },
    { id: 'posiciones', label: 'Posiciones', icon: Medal },
    { id: 'goleadores', label: 'Goleadores', icon: Target },
    { id: 'tarjetas', label: 'Tarjetas', icon: CreditCard },
    { id: 'tribunal', label: 'Tribunal', icon: Gavel },
  ];

  if (loading) {
    return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div data-testid="torneo-detail">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} className="text-slate-400 hover:text-white" data-testid="torneo-back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h2 className="text-2xl font-bold text-white">{torneo.nombre}</h2>
            <p className="text-sm text-slate-400">Categoria: {catName} | {torneo.estado === 'activo' ? 'En curso' : 'Finalizado'}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white gap-1"
            onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success('Link copiado!'); }}
            data-testid="copy-public-link">
            <ExternalLink className="w-4 h-4" /> Compartir
          </Button>
          {torneo.estado === 'activo' && (
            <Button variant="destructive" size="sm" onClick={() => setShowFinalizarDialog(true)} data-testid="finalizar-torneo-btn">
              Finalizar Torneo
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-slate-800 p-1 rounded-lg overflow-x-auto">
        {TABS.map(tab => {
          const Icon = tab.icon;
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === tab.id ? 'bg-white text-slate-900' : 'text-slate-400 hover:text-white'
              }`}
              data-testid={`torneo-tab-${tab.id}`}>
              <Icon className="w-4 h-4" /> {tab.label}
            </button>
          );
        })}
      </div>

      {/* Fixture Tab */}
      {activeTab === 'fechas' && (
        <div>
          {torneo.estado === 'activo' && (
            <Button onClick={() => setShowFechaDialog(true)} className="gap-2 bg-white text-slate-900 hover:bg-slate-200 mb-4" data-testid="add-fecha-btn">
              <Plus className="w-4 h-4" /> Agregar Fecha
            </Button>
          )}
          {fechas.length === 0 ? (
            <Card className="p-6 text-center bg-slate-800 border-slate-700">
              <p className="text-slate-400">Sin fechas. Agrega fechas al torneo.</p>
            </Card>
          ) : (
            <div className="space-y-3">
              {fechas.map(fecha => (
                <Card key={fecha.id} className="p-4 bg-slate-800 border-slate-700" data-testid={`torneo-fecha-${fecha.id}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="font-semibold text-white">{fecha.nombre}</h4>
                      <div className="flex gap-2 text-xs text-slate-400 mt-1">
                        <span className={`px-1.5 py-0.5 rounded ${fecha.tipo === 'eliminacion' ? 'bg-purple-900/50 text-purple-400' : 'bg-blue-900/50 text-blue-400'}`}>
                          {fecha.tipo === 'eliminacion' ? 'Eliminacion' : 'Grupo'}
                        </span>
                        {fecha.grupo && <span className="bg-slate-700 px-1.5 py-0.5 rounded">Grupo {fecha.grupo}</span>}
                        {fecha.fecha_programada && <span>{fecha.fecha_programada}</span>}
                        {fecha.cancha && <span>| {fecha.cancha}</span>}
                        {fecha.hora && <span>| {fecha.hora}</span>}
                        {fecha.estado === 'suspendida' && (
                          <span className="bg-red-900/50 text-red-400 px-1.5 py-0.5 rounded">Suspendida: {fecha.suspendida_razon}</span>
                        )}
                      </div>
                    </div>
                    {torneo.estado === 'activo' && (
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" className="text-slate-400 hover:text-white"
                          onClick={() => { setSelectedFecha(fecha); setShowPartidoDialog(true); }}>
                          <Plus className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300" onClick={() => handleDeleteFecha(fecha.id)}>
                          <Trash className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                  <FechaPartidos
                    fechaId={fecha.id}
                    torneoActivo={torneo.estado === 'activo'}
                    onResultado={(p) => { setSelectedPartido(p); setShowResultadoDialog(true); }}
                    onEvento={(p) => { setSelectedPartido(p); setShowEventoDialog(true); }}
                    onDelete={handleDeletePartido}
                    onRefresh={loadAll}
                  />
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Posiciones Tab */}
      {activeTab === 'posiciones' && (
        <div>
          {posiciones.length === 0 ? (
            <Card className="p-6 text-center bg-slate-800 border-slate-700">
              <p className="text-slate-400">Sin datos. Los partidos deben estar finalizados.</p>
            </Card>
          ) : (
            <Card className="overflow-hidden border-0 shadow-md">
              <table className="w-full text-sm" data-testid="torneo-posiciones-table">
                <thead>
                  <tr className="bg-slate-800 text-white text-left">
                    <th className="px-3 py-3 w-12 text-center">#</th>
                    <th className="px-3 py-3">Equipo</th>
                    <th className="px-3 py-3 text-center">PJ</th>
                    <th className="px-3 py-3 text-center">PG</th>
                    <th className="px-3 py-3 text-center">PE</th>
                    <th className="px-3 py-3 text-center">PP</th>
                    <th className="px-3 py-3 text-center">GF</th>
                    <th className="px-3 py-3 text-center">GC</th>
                    <th className="px-3 py-3 text-center">DIF</th>
                    <th className="px-3 py-3 text-center font-bold">PTS</th>
                  </tr>
                </thead>
                <tbody>
                  {posiciones.map(eq => (
                    <tr key={eq.equipo} className="border-b border-slate-700 hover:bg-slate-800/50">
                      <td className="px-3 py-2.5 text-center font-bold text-slate-400">{eq.pos}</td>
                      <td className="px-3 py-2.5 font-semibold text-white">{eq.equipo}</td>
                      <td className="px-3 py-2.5 text-center text-slate-400">{eq.pj}</td>
                      <td className="px-3 py-2.5 text-center text-emerald-400">{eq.pg}</td>
                      <td className="px-3 py-2.5 text-center text-amber-400">{eq.pe}</td>
                      <td className="px-3 py-2.5 text-center text-red-400">{eq.pp}</td>
                      <td className="px-3 py-2.5 text-center text-slate-400">{eq.gf}</td>
                      <td className="px-3 py-2.5 text-center text-slate-400">{eq.gc}</td>
                      <td className="px-3 py-2.5 text-center font-medium">
                        <span className={eq.dif > 0 ? 'text-emerald-400' : eq.dif < 0 ? 'text-red-400' : 'text-slate-500'}>
                          {eq.dif > 0 ? `+${eq.dif}` : eq.dif}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-center font-bold text-lg text-white">{eq.pts}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </div>
      )}

      {/* Goleadores Tab */}
      {activeTab === 'goleadores' && (
        <div>
          {goleadores.length === 0 ? (
            <Card className="p-6 text-center bg-slate-800 border-slate-700"><p className="text-slate-400">Sin goles registrados.</p></Card>
          ) : (
            <Card className="overflow-hidden border-0 shadow-md">
              <table className="w-full text-sm" data-testid="torneo-goleadores-table">
                <thead><tr className="bg-slate-800 text-white"><th className="px-3 py-3 w-12 text-center">#</th><th className="px-3 py-3">Jugador</th><th className="px-3 py-3">Equipo</th><th className="px-3 py-3 text-center">Goles</th></tr></thead>
                <tbody>
                  {goleadores.map((g, i) => (
                    <tr key={i} className="border-b border-slate-700 hover:bg-slate-800/50">
                      <td className="px-3 py-2.5 text-center font-bold text-slate-400">{g.pos}</td>
                      <td className="px-3 py-2.5 font-semibold text-white">{g.jugador}</td>
                      <td className="px-3 py-2.5 text-slate-400">{g.equipo}</td>
                      <td className="px-3 py-2.5 text-center font-bold text-lg text-white">{g.goles}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </div>
      )}

      {/* Tarjetas Tab */}
      {activeTab === 'tarjetas' && (
        <div>
          {tarjetas.length === 0 ? (
            <Card className="p-6 text-center bg-slate-800 border-slate-700"><p className="text-slate-400">Sin tarjetas registradas.</p></Card>
          ) : (
            <Card className="overflow-hidden border-0 shadow-md">
              <table className="w-full text-sm" data-testid="torneo-tarjetas-table">
                <thead><tr className="bg-slate-800 text-white"><th className="px-3 py-3">Jugador</th><th className="px-3 py-3">Equipo</th><th className="px-3 py-3 text-center">Amarillas</th><th className="px-3 py-3 text-center">Rojas</th></tr></thead>
                <tbody>
                  {tarjetas.map((t, i) => (
                    <tr key={i} className="border-b border-slate-700 hover:bg-slate-800/50">
                      <td className="px-3 py-2.5 font-semibold text-white">{t.jugador}</td>
                      <td className="px-3 py-2.5 text-slate-400">{t.equipo}</td>
                      <td className="px-3 py-2.5 text-center"><span className="bg-amber-500 text-black px-2 py-0.5 rounded font-bold text-xs">{t.amarillas}</span></td>
                      <td className="px-3 py-2.5 text-center">{t.rojas > 0 && <span className="bg-red-600 text-white px-2 py-0.5 rounded font-bold text-xs">{t.rojas}</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </div>
      )}

      {/* Tribunal Tab */}
      {activeTab === 'tribunal' && (
        <div>
          {torneo.estado === 'activo' && (
            <Button onClick={() => setShowSancionDialog(true)} className="gap-2 bg-white text-slate-900 hover:bg-slate-200 mb-4" data-testid="add-sancion-btn">
              <Plus className="w-4 h-4" /> Nueva Sancion
            </Button>
          )}
          {sanciones.length === 0 ? (
            <Card className="p-6 text-center bg-slate-800 border-slate-700"><p className="text-slate-400">Sin sanciones registradas.</p></Card>
          ) : (
            <div className="space-y-3">
              {sanciones.map(s => (
                <Card key={s.id} className={`p-4 bg-slate-800 border-slate-700 ${s.activa ? 'border-l-4 border-l-red-500' : 'opacity-60'}`} data-testid={`sancion-${s.id}`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold text-white">{s.jugador_nombre} <span className="text-slate-400 font-normal">({s.equipo})</span></p>
                      <p className="text-sm text-slate-400 mt-1">{s.causa}</p>
                      <div className="flex gap-3 mt-2 text-xs">
                        <span className={`px-2 py-0.5 rounded ${s.tipo === 'tribunal' ? 'bg-purple-900/50 text-purple-400' : s.tipo === 'roja' ? 'bg-red-900/50 text-red-400' : 'bg-amber-900/50 text-amber-400'}`}>
                          {s.tipo === 'tribunal' ? 'Tribunal' : s.tipo === 'roja' ? 'Roja directa' : 'Amarillas acumuladas'}
                        </span>
                        <span className="text-slate-400">
                          {s.fechas_cumplidas}/{s.fechas_castigo} fechas cumplidas
                        </span>
                        <span className={s.activa ? 'text-red-400' : 'text-emerald-400'}>
                          {s.activa ? 'Activa' : 'Cumplida'}
                        </span>
                      </div>
                    </div>
                    {torneo.estado === 'activo' && s.activa && (
                      <Button size="sm" variant="ghost" className="text-slate-400 hover:text-white"
                        onClick={async () => {
                          await axios.put(`${API}/torneos/sanciones/${s.id}`, { activa: false, fechas_cumplidas: s.fechas_castigo }, { withCredentials: true });
                          toast.success('Sancion marcada como cumplida');
                          loadAll();
                        }}>
                        Cumplida
                      </Button>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Dialogs */}
      <FechaDialog open={showFechaDialog} onClose={() => setShowFechaDialog(false)} torneoId={torneo.id} onCreated={() => { setShowFechaDialog(false); loadAll(); }} />
      <PartidoDialog open={showPartidoDialog} onClose={() => { setShowPartidoDialog(false); setSelectedFecha(null); }} fechaId={selectedFecha?.id} equipos={equipos} onCreated={() => { setShowPartidoDialog(false); setSelectedFecha(null); loadAll(); }} />
      <ResultadoDialog open={showResultadoDialog} onClose={() => { setShowResultadoDialog(false); setSelectedPartido(null); }} partido={selectedPartido} onSaved={() => { setShowResultadoDialog(false); setSelectedPartido(null); loadAll(); }} />
      <EventoDialog open={showEventoDialog} onClose={() => { setShowEventoDialog(false); setSelectedPartido(null); }} partido={selectedPartido} equipos={equipos} onSaved={() => { setShowEventoDialog(false); setSelectedPartido(null); loadAll(); }} />
      <SancionDialog open={showSancionDialog} onClose={() => setShowSancionDialog(false)} torneoId={torneo.id} equipos={equipos} onCreated={() => { setShowSancionDialog(false); loadAll(); }} />
      <FinalizarTorneoDialog open={showFinalizarDialog} onClose={() => setShowFinalizarDialog(false)} torneoId={torneo.id} equipos={equipos} onFinalizado={() => { setShowFinalizarDialog(false); onBack(); }} />
    </div>
  );
}


function FechaPartidos({ fechaId, torneoActivo, onResultado, onEvento, onDelete, onRefresh }) {
  const [partidos, setPartidos] = useState([]);
  useEffect(() => { load(); }, [fechaId]);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/torneos/fechas/${fechaId}/partidos`, { withCredentials: true });
      setPartidos(res.data);
    } catch {}
  };

  if (partidos.length === 0) return <p className="text-xs text-slate-500 pl-2">Sin partidos</p>;

  return (
    <div className="space-y-2">
      {partidos.map(p => (
        <div key={p.id} className="flex items-center justify-between p-2 bg-slate-700 rounded" data-testid={`partido-${p.id}`}>
          <div className="flex-1">
            <span className="text-sm font-medium text-white">{p.equipo_local}</span>
            {p.estado === 'finalizado' ? (
              <span className="mx-2 text-emerald-400 font-bold">{p.goles_local} - {p.goles_visitante}</span>
            ) : (
              <span className="mx-2 text-slate-500">vs</span>
            )}
            <span className="text-sm font-medium text-white">{p.equipo_visitante}</span>
            {p.estado === 'finalizado' && <span className="ml-2 text-xs text-slate-500">Final</span>}
            {p.eventos?.length > 0 && (
              <div className="flex gap-1 mt-1">
                {p.eventos.map(e => (
                  <span key={e.id} className={`text-xs px-1 py-0.5 rounded ${e.tipo === 'gol' ? 'bg-emerald-900/50 text-emerald-400' : e.tipo === 'amarilla' ? 'bg-amber-500 text-black' : 'bg-red-600 text-white'}`}>
                    {e.tipo === 'gol' ? `${e.jugador_nombre} ${e.minuto ? e.minuto + "'" : ''}` : `${e.jugador_nombre}`}
                  </span>
                ))}
              </div>
            )}
          </div>
          {torneoActivo && (
            <div className="flex gap-1 shrink-0 ml-2">
              <Button size="sm" variant="ghost" className="text-slate-400 hover:text-white h-7 px-2" onClick={() => onResultado(p)} title="Resultado">
                <Medal className="w-3.5 h-3.5" />
              </Button>
              <Button size="sm" variant="ghost" className="text-slate-400 hover:text-white h-7 px-2" onClick={() => onEvento(p)} title="Evento">
                <Target className="w-3.5 h-3.5" />
              </Button>
              <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300 h-7 px-2" onClick={() => onDelete(p.id)} title="Eliminar">
                <Trash className="w-3.5 h-3.5" />
              </Button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}


function FechaDialog({ open, onClose, torneoId, onCreated }) {
  const [nombre, setNombre] = useState('');
  const [tipo, setTipo] = useState('grupo');
  const [grupo, setGrupo] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/torneos/${torneoId}/fechas`, { nombre, tipo, grupo: grupo || null }, { withCredentials: true });
      toast.success('Fecha creada');
      setNombre(''); setTipo('grupo'); setGrupo('');
      onCreated();
    } catch (error) { toast.error('Error'); } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-800 border-slate-700">
        <DialogHeader><DialogTitle className="text-white">Nueva Fecha</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div><Label className="text-slate-300">Nombre</Label><Input value={nombre} onChange={(e) => setNombre(e.target.value)} required className="bg-slate-700 border-slate-600 text-white" placeholder="Ej: Fecha 1, Cuartos de Final" data-testid="torneo-fecha-nombre" /></div>
          <div><Label className="text-slate-300">Tipo</Label>
            <Select value={tipo} onValueChange={setTipo}><SelectTrigger className="bg-slate-700 border-slate-600 text-white"><SelectValue /></SelectTrigger><SelectContent position="popper" sideOffset={5}><SelectItem value="grupo">Fase de Grupo</SelectItem><SelectItem value="eliminacion">Eliminacion</SelectItem></SelectContent></Select>
          </div>
          {tipo === 'grupo' && <div><Label className="text-slate-300">Grupo (opcional)</Label><Input value={grupo} onChange={(e) => setGrupo(e.target.value)} className="bg-slate-700 border-slate-600 text-white" placeholder="Ej: A, B" /></div>}
          <Button type="submit" className="w-full bg-white text-slate-900 hover:bg-slate-200" disabled={loading}>{loading ? 'Creando...' : 'Crear Fecha'}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}


function PartidoDialog({ open, onClose, fechaId, equipos, onCreated }) {
  const [local, setLocal] = useState('');
  const [visitante, setVisitante] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (local === visitante) { toast.error('Los equipos deben ser diferentes'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/torneos/fechas/${fechaId}/partidos`, { equipo_local: local, equipo_visitante: visitante }, { withCredentials: true });
      toast.success('Partido agregado');
      setLocal(''); setVisitante('');
      onCreated();
    } catch (error) { toast.error('Error'); } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-800 border-slate-700">
        <DialogHeader><DialogTitle className="text-white">Agregar Partido</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div><Label className="text-slate-300">Equipo Local</Label>
            <Select value={local} onValueChange={setLocal}><SelectTrigger className="bg-slate-700 border-slate-600 text-white" data-testid="partido-local-select"><SelectValue placeholder="Selecciona" /></SelectTrigger><SelectContent position="popper" sideOffset={5}>{equipos.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent></Select>
          </div>
          <div><Label className="text-slate-300">Equipo Visitante</Label>
            <Select value={visitante} onValueChange={setVisitante}><SelectTrigger className="bg-slate-700 border-slate-600 text-white" data-testid="partido-visitante-select"><SelectValue placeholder="Selecciona" /></SelectTrigger><SelectContent position="popper" sideOffset={5}>{equipos.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent></Select>
          </div>
          <Button type="submit" className="w-full bg-white text-slate-900 hover:bg-slate-200" disabled={loading || !local || !visitante}>{loading ? 'Guardando...' : 'Agregar Partido'}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}


function ResultadoDialog({ open, onClose, partido, onSaved }) {
  const [golesLocal, setGolesLocal] = useState(0);
  const [golesVisitante, setGolesVisitante] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (partido) {
      setGolesLocal(partido.goles_local || 0);
      setGolesVisitante(partido.goles_visitante || 0);
    }
  }, [partido]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/torneos/partidos/${partido.id}/resultado`, { goles_local: golesLocal, goles_visitante: golesVisitante }, { withCredentials: true });
      toast.success('Resultado registrado');
      onSaved();
    } catch (error) { toast.error('Error'); } finally { setLoading(false); }
  };

  if (!partido) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-800 border-slate-700">
        <DialogHeader><DialogTitle className="text-white">Resultado del Partido</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-3 gap-4 items-center">
            <div className="text-center">
              <p className="text-sm text-slate-400 mb-2">Local</p>
              <p className="font-bold text-white mb-2">{partido.equipo_local}</p>
              <Input type="number" min={0} value={golesLocal} onChange={(e) => setGolesLocal(parseInt(e.target.value) || 0)}
                className="bg-slate-700 border-slate-600 text-white text-center text-2xl font-bold" data-testid="resultado-local" />
            </div>
            <div className="text-center text-2xl text-slate-500 font-bold pt-6">vs</div>
            <div className="text-center">
              <p className="text-sm text-slate-400 mb-2">Visitante</p>
              <p className="font-bold text-white mb-2">{partido.equipo_visitante}</p>
              <Input type="number" min={0} value={golesVisitante} onChange={(e) => setGolesVisitante(parseInt(e.target.value) || 0)}
                className="bg-slate-700 border-slate-600 text-white text-center text-2xl font-bold" data-testid="resultado-visitante" />
            </div>
          </div>
          <Button type="submit" className="w-full bg-white text-slate-900 hover:bg-slate-200" disabled={loading}>{loading ? 'Guardando...' : 'Registrar Resultado'}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}


function EventoDialog({ open, onClose, partido, equipos, onSaved }) {
  const [tipo, setTipo] = useState('gol');
  const [jugador, setJugador] = useState('');
  const [equipo, setEquipo] = useState('');
  const [minuto, setMinuto] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (partido) {
      setEquipo(partido.equipo_local);
    }
  }, [partido]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/torneos/partidos/${partido.id}/eventos`, {
        tipo, jugador_nombre: jugador, equipo, minuto: minuto ? parseInt(minuto) : null
      }, { withCredentials: true });
      toast.success('Evento registrado');
      setJugador(''); setMinuto('');
      onSaved();
    } catch (error) { toast.error('Error'); } finally { setLoading(false); }
  };

  if (!partido) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-800 border-slate-700">
        <DialogHeader><DialogTitle className="text-white">Registrar Evento</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div><Label className="text-slate-300">Tipo</Label>
            <Select value={tipo} onValueChange={setTipo}><SelectTrigger className="bg-slate-700 border-slate-600 text-white"><SelectValue /></SelectTrigger><SelectContent position="popper" sideOffset={5}><SelectItem value="gol">Gol</SelectItem><SelectItem value="amarilla">Tarjeta Amarilla</SelectItem><SelectItem value="roja">Tarjeta Roja</SelectItem></SelectContent></Select>
          </div>
          <div><Label className="text-slate-300">Equipo</Label>
            <Select value={equipo} onValueChange={setEquipo}><SelectTrigger className="bg-slate-700 border-slate-600 text-white"><SelectValue /></SelectTrigger><SelectContent position="popper" sideOffset={5}><SelectItem value={partido.equipo_local}>{partido.equipo_local}</SelectItem><SelectItem value={partido.equipo_visitante}>{partido.equipo_visitante}</SelectItem></SelectContent></Select>
          </div>
          <div><Label className="text-slate-300">Nombre del Jugador</Label><Input value={jugador} onChange={(e) => setJugador(e.target.value)} required className="bg-slate-700 border-slate-600 text-white" placeholder="Nombre completo" data-testid="evento-jugador" /></div>
          <div><Label className="text-slate-300">Minuto (opcional)</Label><Input type="number" min={0} max={120} value={minuto} onChange={(e) => setMinuto(e.target.value)} className="bg-slate-700 border-slate-600 text-white" placeholder="Ej: 35" /></div>
          <Button type="submit" className="w-full bg-white text-slate-900 hover:bg-slate-200" disabled={loading || !jugador}>{loading ? 'Guardando...' : 'Registrar Evento'}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}


function SancionDialog({ open, onClose, torneoId, equipos, onCreated }) {
  const [jugador, setJugador] = useState('');
  const [equipo, setEquipo] = useState('');
  const [causa, setCausa] = useState('');
  const [fechas, setFechas] = useState(1);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/torneos/${torneoId}/sanciones`, {
        jugador_nombre: jugador, equipo, tipo: 'tribunal', causa, fechas_castigo: fechas
      }, { withCredentials: true });
      toast.success('Sancion creada');
      setJugador(''); setEquipo(''); setCausa(''); setFechas(1);
      onCreated();
    } catch (error) { toast.error('Error'); } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-800 border-slate-700">
        <DialogHeader><DialogTitle className="text-white">Sancion del Tribunal</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div><Label className="text-slate-300">Jugador</Label><Input value={jugador} onChange={(e) => setJugador(e.target.value)} required className="bg-slate-700 border-slate-600 text-white" placeholder="Nombre del jugador" data-testid="sancion-jugador" /></div>
          <div><Label className="text-slate-300">Equipo</Label>
            <Select value={equipo} onValueChange={setEquipo}><SelectTrigger className="bg-slate-700 border-slate-600 text-white" data-testid="sancion-equipo"><SelectValue placeholder="Selecciona equipo" /></SelectTrigger><SelectContent position="popper" sideOffset={5}>{equipos.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent></Select>
          </div>
          <div><Label className="text-slate-300">Causa</Label><Input value={causa} onChange={(e) => setCausa(e.target.value)} required className="bg-slate-700 border-slate-600 text-white" placeholder="Motivo de la sancion" data-testid="sancion-causa" /></div>
          <div><Label className="text-slate-300">Fechas de castigo</Label><Input type="number" min={1} max={50} value={fechas} onChange={(e) => setFechas(parseInt(e.target.value))} className="bg-slate-700 border-slate-600 text-white" data-testid="sancion-fechas" /></div>
          <Button type="submit" className="w-full bg-white text-slate-900 hover:bg-slate-200" disabled={loading || !equipo || !jugador}>{loading ? 'Guardando...' : 'Crear Sancion'}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}


function FinalizarTorneoDialog({ open, onClose, torneoId, equipos, onFinalizado }) {
  const [motivo, setMotivo] = useState('campeon');
  const [campeon, setCampeon] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (motivo === 'campeon' && !campeon) { toast.error('Selecciona el campeon'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/torneos/${torneoId}/finalizar`, {
        campeon: motivo === 'campeon' ? campeon : null,
        motivo
      }, { withCredentials: true });
      toast.success('Torneo finalizado');
      onFinalizado();
    } catch (error) { toast.error('Error'); } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-800 border-slate-700">
        <DialogHeader><DialogTitle className="text-white">Finalizar Torneo</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div><Label className="text-slate-300">Motivo</Label>
            <Select value={motivo} onValueChange={setMotivo}><SelectTrigger className="bg-slate-700 border-slate-600 text-white"><SelectValue /></SelectTrigger><SelectContent position="popper" sideOffset={5}><SelectItem value="campeon">Hay un campeon</SelectItem><SelectItem value="forzado">Finalizacion forzada</SelectItem></SelectContent></Select>
          </div>
          {motivo === 'campeon' && (
            <div><Label className="text-slate-300">Campeon</Label>
              <Select value={campeon} onValueChange={setCampeon}><SelectTrigger className="bg-slate-700 border-slate-600 text-white" data-testid="campeon-select"><SelectValue placeholder="Selecciona el campeon" /></SelectTrigger><SelectContent position="popper" sideOffset={5}>{equipos.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent></Select>
            </div>
          )}
          {motivo === 'forzado' && (
            <div className="p-3 bg-red-900/30 border border-red-800 rounded-lg">
              <p className="text-sm text-red-400"><AlertTriangle className="w-4 h-4 inline mr-1" />El torneo se finalizara sin campeon. Esta accion no se puede deshacer.</p>
            </div>
          )}
          <Button type="submit" variant="destructive" className="w-full" disabled={loading}>{loading ? 'Finalizando...' : 'Finalizar Torneo'}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
